import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import '../../../styles/mpProductCategory.scss';
import { tryFetch } from 'Helpers/FetchAPI';
import SkeletonComponent from 'Components/Skeleton';

interface Product {
  id: string;
  store_id: string;
  block_id: string;
  product_name: string;
  product_type: string;
  product_description: string;
  specifications: any[];
  product_slug: string;
  unit_price: string;
  is_sale: boolean;
  sale_price: string;
  stock_count: number;
  status: string;
  created_at?: string;
  legal_age: string;
  merchant_slug: string;
  media: any[]
}

interface Merchant {
  id: string;
  store_name: string;
  merchant_slug: string;
  store_logo?: string;
}

const ProductCategory: React.FC = () => {
  const { merchant_slug = '', productCategoryName = '' } = useParams();
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortAZ, setSortAZ] = useState<'a-z' | 'z-a'>('a-z');
  const [sortDate, setSortDate] = useState<'newest' | 'oldest'>('newest');
  const [sortPrice, setSortPrice] = useState<'low-high' | 'high-low'>('low-high');
  const [selectedProductSlug, setSelectedProductSlug] = useState<string | null>(null);
  const [showAgeVerification, setShowAgeVerification] = useState(false);
  const [loadedImages, setLoadedImages] = useState<{ [key: string]: boolean }>({});
  const [activeFilter, setActiveFilter] = useState<'all' | 'az' | 'date' | 'price'>('all');
  const [merchantName, setMerchantName] = useState('');
  const navigate = useNavigate();

  const formattedTitle = productCategoryName.replace(/-/g, ' ');

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        // const merchantRes = await tryFetch({ url: `/merchants`, method: 'GET' });
        // if (!merchantRes.is_success || !Array.isArray(merchantRes.data)) {
        //   throw new Error('Invalid merchant data');
        // }

        if (merchant_slug === 'a' && formattedTitle.toLowerCase() === 'gottsulit') {
          const merchantRes = await tryFetch({ url: `/merchants`, method: 'GET' });
          if (!merchantRes.is_success || !Array.isArray(merchantRes.data)) {
            throw new Error('Invalid merchant data');
          }

          const merchants = merchantRes.data as Merchant[];
          let allSaleProducts: Product[] = [];

          for (const merchant of merchants) {
            const productRes = await tryFetch({
              url: `/merchants/${merchant.id}/products?limit=999`,
              method: 'GET',
            });

            if (productRes.is_success && Array.isArray(productRes.data)) {
              const saleProducts = (productRes.data as Product[]).filter(p => p.is_sale);
              allSaleProducts = [...allSaleProducts, ...saleProducts];
            }
          }

          setProducts(allSaleProducts);
          setFilteredProducts(allSaleProducts);
          setMerchantName('GottSulit');
          setLoading(false);
          return;
        }

        const merchantRes = await tryFetch({ url: `/merchants`, method: 'GET' });
        if (!merchantRes.is_success || !Array.isArray(merchantRes.data)) {
          throw new Error('Invalid merchant data');
        }

        const merchants = merchantRes.data as Merchant[];
        const merchant = merchants.find((m) => m.merchant_slug === merchant_slug);
        if (!merchant) throw new Error('Merchant not found');
        const formattedName = merchant.merchant_slug
            .split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');

            setMerchantName(formattedName);

        const productRes = await tryFetch({
          url: `/merchants/${merchant.id}/products?limit=999`,
          method: 'GET',
        });
        if (!productRes.is_success || !Array.isArray(productRes.data)) {
          throw new Error('Invalid product data');
        }

        let allProducts = productRes.data as Product[];

        if (formattedTitle.toLowerCase() === 'gottsulit') {
          allProducts = allProducts.filter(p => p.is_sale);
         } else if (formattedTitle.toLowerCase() !== 'products') {
          allProducts = allProducts.filter(p =>
            p.product_type.toLowerCase() === formattedTitle.toLowerCase()
          );
        }

        setProducts(allProducts);
        setFilteredProducts(allProducts);
        setLoading(false);
      } catch (err) {
        console.error(err);
        setLoading(false);
      }
    };

    fetchProducts();
  }, [merchant_slug, productCategoryName]);

  const handleAll = () => {
  setFilteredProducts(products);
  setActiveFilter('all');
};

  const handleSortAZ = () => {
    const newSort = sortAZ === 'a-z' ? 'z-a' : 'a-z';
    setSortAZ(newSort);
    setActiveFilter('az');
    const sorted = [...filteredProducts].sort((a, b) =>
      newSort === 'a-z'
        ? a.product_name.localeCompare(b.product_name)
        : b.product_name.localeCompare(a.product_name)
    );
    setFilteredProducts(sorted);
  };

  const handleSortDate = () => {
    const newSort = sortDate === 'newest' ? 'oldest' : 'newest';
    setSortDate(newSort);
    setActiveFilter('date');
    const sorted = [...filteredProducts].sort((a, b) => {
      const dateA = new Date(a.created_at || '').getTime();
      const dateB = new Date(b.created_at || '').getTime();
      return newSort === 'newest' ? dateB - dateA : dateA - dateB;
    });
    setFilteredProducts(sorted);
  };

  const handleSortPrice = () => {
    const newSort = sortPrice === 'low-high' ? 'high-low' : 'low-high';
    setSortPrice(newSort);
     setActiveFilter('price');
    const sorted = [...filteredProducts].sort((a, b) => {
      const priceA = parseFloat(a.is_sale ? a.sale_price : a.unit_price);
      const priceB = parseFloat(b.is_sale ? b.sale_price : b.unit_price);
      return newSort === 'low-high' ? priceA - priceB : priceB - priceA;
    });
    setFilteredProducts(sorted);
  };

    const handleProductClick = (product: Product) => {
      if (product.legal_age) {
        setSelectedProductSlug(product.product_slug);
        setShowAgeVerification(true);
      } else {
        navigate(`/merchant/${product.merchant_slug}/product/${product.product_slug}`);
      }
    };

    const handleImageLoad = (imageKey: string) => {
    setLoadedImages((prevState) => ({
      ...prevState,
      [imageKey]: true,
    }));
  };

  const pageTitle = formattedTitle.toLowerCase() === 'products'
    ? `${merchantName}'s Products`
    : formattedTitle;

  return (
    <div className="category-page">
      <h2 className="category-title">{pageTitle}</h2>

      <div className="filter-tabs">
        <button
            onClick={handleAll}
            className={activeFilter === 'all' ? 'active' : ''}
        >
            All
        </button>
        <button onClick={handleSortAZ}
        className={activeFilter === 'az' ? 'active' : ''}>
          {sortAZ === 'a-z' ? 'A → Z' : 'Z → A'}
        </button>
        <button onClick={handleSortDate}
        className={activeFilter === 'date' ? 'active' : ''}>
          {sortDate === 'newest' ? 'Newest → Oldest' : 'Oldest → Newest'}
        </button>
        <button onClick={handleSortPrice}
        className={activeFilter === 'price' ? 'active' : ''}>
          {sortPrice === 'low-high' ? 'Lowest → Highest' : 'Highest → Lowest'}
        </button>
      </div>

      <div className="product-grid">
        {loading
          ? Array(6)
              .fill(0)
              .map((_, i) => <SkeletonComponent key={i} />)
          : filteredProducts.map((product) => (
              <Link
                key={product.id}
                to={`/merchant/${merchant_slug}/product/${product.product_slug}`}
                className="product-card"
              >
                 <div className="product-image" onClick={() => handleProductClick(product)} style={{ cursor: 'pointer' }}>
              <img
                src={product.media[0]?.media_url}
                alt={product.product_name}
                width="169"
                height="169"
                className={loadedImages[`sale-${product.block_id}`] ? 'loaded' : ''}
                onLoad={() => handleImageLoad(`sale-${product.block_id}`)}
              />
            </div>
                <p className="product-name">
                    {product.product_name.length > 25
                      ? product.product_name.slice(0, 25) + '...'
                      : product.product_name}
                  </p>
                <div className="product-price">
                  {product.is_sale ? (
                    <>
                      <span className="sale-price">₱{product.sale_price}</span>
                      <span className="old-price">₱{product.unit_price}</span>
                    </>
                  ) : (
                    <>₱{product.unit_price}</>
                  )}
                </div>
              </Link>
            ))}
      </div>
    </div>
  );
};

export default ProductCategory;
